import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {KairosMark} from '@/components/kairos-mark'
import {SectionCard} from '@/components/section-card'
import {AprobarSheet} from '@/components/aprobar-sheet'
import {useRejectEmployee} from '@/hooks/sgsst/riesgos/use-entrevista'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {translateApiError} from '@/lib/errors'

/** Rejecting soft-deletes only this membership: the person's global account and other
 * businesses are untouched. */
export function PendientesAprobacion({
  businessId,
  pendientes,
  canEdit,
  canAssignResponsable,
}: {
  businessId: string
  pendientes: Employee[]
  canEdit: boolean
  /** Mirrors `_reject_role_escalation`: a responsable can't grant a role above their own. */
  canAssignResponsable: boolean
}) {
  const {t} = useTranslation()
  const reject = useRejectEmployee(businessId)
  const [approveTarget, setApproveTarget] = useState<Employee | null>(null)
  const [rejectTarget, setRejectTarget] = useState<Employee | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (pendientes.length === 0) return null

  function fullName(e: Employee) {
    return `${e.name} ${e.last_name}`
  }

  return (
    <SectionCard
      testId="pendientes-aprobacion"
      bodyClassName="p-0"
      title={
        <span className="inline-flex items-center gap-2">
          <KairosMark />
          {t('employees.pendientes.title')}
          <Tag tone="amber">{pendientes.length}</Tag>
        </span>
      }>
      <div className="divide-y divide-border">
        {pendientes.map((e) => (
          <div key={e.id} data-testid="pendiente-row" className="flex items-center justify-between gap-4 px-5 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{fullName(e)}</p>
              <p className="font-mono text-xs text-muted-foreground">{e.cedula}</p>
            </div>
            {canEdit && (
              <div className="flex shrink-0 gap-1.5">
                <Button size="sm" data-testid={`pendiente-${e.id}-aprobar`} onClick={() => setApproveTarget(e)}>
                  {t('employees.pendientes.approve')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`pendiente-${e.id}-rechazar`}
                  onClick={() => setRejectTarget(e)}>
                  {t('employees.pendientes.reject')}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {error && <p className="px-5 py-3 text-sm text-destructive">{error}</p>}

      <AprobarSheet
        businessId={businessId}
        employee={approveTarget}
        canAssignResponsable={canAssignResponsable}
        onOpenChange={(open) => !open && setApproveTarget(null)}
        onApproved={() => setApproveTarget(null)}
      />
      <ConfirmDialog
        open={!!rejectTarget}
        onOpenChange={(open) => !open && setRejectTarget(null)}
        title={t('employees.pendientes.rejectConfirmTitle', {name: rejectTarget ? fullName(rejectTarget) : ''})}
        description={t('employees.pendientes.rejectConfirmBody', {name: rejectTarget ? fullName(rejectTarget) : ''})}
        porque={{
          label: t('employees.pendientes.rejectPorqueLabel'),
          testId: 'pendientes-reject-porque',
          contenidoTestId: 'pendientes-reject-porque-contenido',
          contenido: <p>{t('employees.pendientes.rejectPorqueBody')}</p>,
        }}
        confirmLabel={t('employees.pendientes.rejectConfirmAction')}
        cancelLabel={t('common.cancel')}
        pending={reject.isPending}
        onConfirm={() => {
          if (!rejectTarget) return
          setError(null)
          reject.mutate(rejectTarget.id, {
            onSuccess: () => setRejectTarget(null),
            onError: (err) => setError(translateApiError(err)),
          })
        }}
      />
    </SectionCard>
  )
}
